import Blob from "cross-blob";
import ExcelJS from "exceljs";
import keyBy from "lodash/keyBy";
import times from "lodash/times";

import {
  getModifiedConfigParamValue,
  ImmigramFormElementType,
} from "@/components/ImmigramForm/elements/common";

import { DocGenerator } from "../index";
import { FormWithDataAndConfig } from "../useGenerateDocFromConfigs";

interface EmploymentXlsxV2Args {
  forms: FormWithDataAndConfig[];
}

const ROUND_BORDER = {
  top: { style: "thin" },
  left: { style: "thin" },
  bottom: { style: "thin" },
  right: { style: "thin" },
} as const;

const FONT = {
  name: "Arial",
};

enum StatusOptions {
  HaveProvided = "have provided",
  HaveNotProvided = "haven't provided",
}

enum ResponsibilityOptions {
  Client = "client",
  Immigram = "Immigram",
}

export const createProfJourneyCheckListXlsx: DocGenerator<
  EmploymentXlsxV2Args
> = async (t, { forms }) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  worksheet.columns = [
    { key: "type", width: 71 },
    { key: "status", width: 24 },
    { key: "responsibility", width: 18 },
  ];

  let rowCounter = 1;

  forms.forEach(({ fields, values }) => {
    const filteredFields = fields?.filter((field) => values[field.name]);

    filteredFields?.forEach((field) => {
      if (field.type === ImmigramFormElementType.Checkbox) {
        const optionsMap = keyBy(field.options, "value");
        const selectedOptions = values[field.name].value
          ?.split(",")
          .map((optionValue) => optionsMap[optionValue]);

        if (!selectedOptions?.length) {
          return;
        }

        worksheet.mergeCells(rowCounter, 1, rowCounter, 3);

        const groupName = field.title
          ? getModifiedConfigParamValue(t, field.title, {
              forceText: true,
            })
          : field.name;

        worksheet.getCell(rowCounter, 1).font = {
          size: 16,
          bold: true,
          ...FONT,
        };
        worksheet.getCell(rowCounter, 1).fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFC9DBF8" },
        };
        worksheet.getCell(rowCounter, 1).alignment = { horizontal: "center" };

        worksheet.getCell(rowCounter, 1).value = groupName;

        worksheet.getRow(rowCounter).commit();

        rowCounter++;
        rowCounter++;

        worksheet.getRow(rowCounter).getCell(1).value = "Type of Document";
        worksheet.getRow(rowCounter).getCell(2).value =
          "Final status of document";
        worksheet.getRow(rowCounter).getCell(3).value = "Responsibility";

        times(3, (i) => {
          worksheet.getRow(rowCounter).getCell(i + 1).font = {
            size: 11,
            bold: true,
            ...FONT,
          };
          worksheet.getRow(rowCounter).getCell(i + 1).border = ROUND_BORDER;
          worksheet.getRow(rowCounter).getCell(i + 1).alignment = {
            wrapText: true,
            vertical: "middle",
            horizontal: "center",
          };
        });

        worksheet.getRow(rowCounter).commit();

        rowCounter++;

        selectedOptions?.forEach((option) => {
          worksheet.getRow(rowCounter).values = {
            type: getModifiedConfigParamValue(t, option?.title, {
              forceText: true,
            }) as string,
            status: StatusOptions.HaveNotProvided,
            responsibility: ResponsibilityOptions.Client,
          };
          worksheet.getCell(rowCounter, 2).dataValidation = {
            type: "list",
            allowBlank: false,
            formulae: [
              `"${StatusOptions.HaveNotProvided}, ${StatusOptions.HaveProvided}"`,
            ],
          };
          worksheet.getCell(rowCounter, 3).dataValidation = {
            type: "list",
            allowBlank: false,
            formulae: [
              `"${ResponsibilityOptions.Client}, ${ResponsibilityOptions.Immigram}"`,
            ],
          };

          times(3, (i) => {
            worksheet.getRow(rowCounter).getCell(i + 1).border = ROUND_BORDER;
            worksheet.getRow(rowCounter).getCell(i + 1).font = {
              size: 11,
              ...FONT,
            };
            worksheet.getRow(rowCounter).getCell(i + 1).alignment = {
              wrapText: true,
            };
          });

          worksheet.getRow(rowCounter).commit();

          rowCounter++;
        });
        rowCounter++;
      }
    });
  });

  worksheet.addConditionalFormatting({
    ref: "B1:B1000",
    rules: [
      {
        priority: 1,
        type: "expression",
        formulae: [`B1="${StatusOptions.HaveNotProvided}"`],
        style: {
          fill: {
            type: "pattern",
            pattern: "solid",
            bgColor: { argb: "FFE6CFF3" },
          },
        },
      },
      {
        priority: 2,
        type: "expression",
        formulae: [`B1="${StatusOptions.HaveProvided}"`],
        style: {
          fill: {
            type: "pattern",
            pattern: "solid",
            bgColor: { argb: "FFD4EDBB" },
          },
        },
      },
    ],
  });

  worksheet.addConditionalFormatting({
    ref: "C1:C1000",
    rules: [
      {
        priority: 3,
        type: "expression",
        formulae: [`C1="${ResponsibilityOptions.Client}"`],
        style: {
          fill: {
            type: "pattern",
            pattern: "solid",
            bgColor: { argb: "FFD4EDBB" },
          },
        },
      },
      {
        priority: 4,
        type: "expression",
        formulae: [`C1="${ResponsibilityOptions.Immigram}"`],
        style: {
          fill: {
            type: "pattern",
            pattern: "solid",
            bgColor: { argb: "FFFFE59F" },
          },
        },
      },
    ],
  });

  return new Blob([await workbook.xlsx.writeBuffer()]);
};
